import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useGetHeaderConfig, useUpdateHeaderConfig } from '../api/layout/useHeaderConfigApi';
import { normalizeSiteHeader, stripServerFields } from '../components/HeaderSettings/siteHeaderModel';

// CMS -> Layout -> Header. The screen edits two things: the logo columns (written by the logo
// upload endpoint too) and `siteHeader`, which holds every other part of the website header.
//
// The legacy columns (enableLanguageSelector, enableAccountMenu, loginUrl, accountDisplayLabel)
// are derived from siteHeader by the server on save, and navigationMenuItems / trustBadges are
// no longer edited, so none of them is read into the form or sent back.

export const defaultConfig = {
  logoUrl: null,
  logoAltText: 'Sunsky',
  logoLinkTarget: '/',
  siteHeader: normalizeSiteHeader(null),
};

const toStringSafe = (value) => (value == null ? '' : String(value));

/**
 * The form state for a GET response. A version 1 siteHeader (and the legacy columns it fell
 * back on) is upgraded exactly as the server's normalizeSiteHeader upgrades it.
 */
export function mapApiToLocal(apiConfig) {
  if (!apiConfig) return { ...defaultConfig, siteHeader: normalizeSiteHeader(null) };
  return {
    logoUrl: apiConfig.logoUrl ?? defaultConfig.logoUrl,
    logoAltText: apiConfig.logoAltText ?? defaultConfig.logoAltText,
    logoLinkTarget: apiConfig.logoLinkTarget ?? defaultConfig.logoLinkTarget,
    siteHeader: normalizeSiteHeader(apiConfig.siteHeader, apiConfig),
  };
}

/** The PUT body: the logo columns and the full settings, minus what only the server computes. */
export function buildUpdatePayload(local) {
  return {
    logoUrl: toStringSafe(local.logoUrl).trim() || null,
    logoAltText: toStringSafe(local.logoAltText).trim(),
    logoLinkTarget: toStringSafe(local.logoLinkTarget).trim() || '/',
    siteHeader: stripServerFields(local.siteHeader),
  };
}

/**
 * An immutable set at a dotted path ('menus.destinations.items'). `value` may be a function of
 * the current value. Array segments are indexes.
 */
export function setIn(obj, path, value) {
  const keys = Array.isArray(path) ? path : String(path).split('.').filter(Boolean);
  if (!keys.length) return typeof value === 'function' ? value(obj) : value;
  const [key, ...rest] = keys;
  if (Array.isArray(obj)) {
    const index = Number(key);
    return obj.map((item, i) => (i === index ? setIn(item, rest, value) : item));
  }
  const base = obj && typeof obj === 'object' ? obj : {};
  return { ...base, [key]: setIn(base[key], rest, value) };
}

/**
 * @param onLoadError called with the error when the first load fails (the screen toasts it)
 */
export function useHeaderConfig({ onLoadError } = {}) {
  const [config, setConfig] = useState(() => mapApiToLocal(null));
  const [lastSaved, setLastSaved] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  const getApi = useGetHeaderConfig();
  const updateApi = useUpdateHeaderConfig();
  const fetchConfig = getApi.execute;
  const putConfig = updateApi.execute;

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const res = await fetchConfig();
      const headerConfig = res?.data?.headerConfig ?? null;
      const mapped = mapApiToLocal(headerConfig);
      setConfig(mapped);
      setLastSaved(mapped);
      return headerConfig;
    } catch (err) {
      if (err.response?.status === 404) {
        const empty = mapApiToLocal(null);
        setConfig(empty);
        setLastSaved(empty);
        return null;
      }
      setLoadFailed(true);
      throw err;
    }
  }, [fetchConfig]);

  useEffect(() => {
    load().catch((err) => onLoadErrorRef.current?.(err));
  }, [load]);

  // Unsaved changes, judged against what was last loaded or saved rather than by "something was
  // typed": switching a block off and on again leaves nothing to save.
  const isDirty = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(lastSaved ?? mapApiToLocal(null)),
    [config, lastSaved]
  );

  /** One logo column: logoUrl, logoAltText or logoLinkTarget. */
  const updateField = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Replace one part of siteHeader, e.g. setSiteHeaderPath('menus.custom', next). */
  const setSiteHeaderPath = useCallback((path, value) => {
    setConfig((prev) => ({ ...prev, siteHeader: setIn(prev.siteHeader, path, value) }));
  }, []);

  /** Merge fields into one block of siteHeader, e.g. patchSiteHeader('support', { label }). */
  const patchSiteHeader = useCallback((path, patch) => {
    setConfig((prev) => ({
      ...prev,
      siteHeader: setIn(prev.siteHeader, path, (current) => ({ ...(current || {}), ...patch })),
    }));
  }, []);

  const save = useCallback(async () => {
    const payload = buildUpdatePayload(config);
    const res = await putConfig(payload);
    const updated = res?.data?.headerConfig ?? null;
    const mapped = updated ? mapApiToLocal(updated) : config;
    setConfig(mapped);
    setLastSaved(mapped);
    return updated;
  }, [config, putConfig]);

  const reset = useCallback(() => {
    if (lastSaved) {
      setConfig(lastSaved);
      return Promise.resolve(lastSaved);
    }
    return load();
  }, [lastSaved, load]);

  return {
    config,
    // Until the first answer arrives the form would show defaults, not the saved settings.
    loading: getApi.loading || (lastSaved === null && !loadFailed),
    saving: updateApi.loading,
    error: getApi.error || updateApi.error,
    // Only a failed load, which the screen offers to retry; a failed save is a toast.
    loadError: loadFailed ? getApi.error || 'Failed to load header settings' : null,
    isDirty,
    // False while the saved settings are unknown, when a save would overwrite them with defaults.
    loaded: lastSaved !== null,
    updateField,
    setSiteHeaderPath,
    patchSiteHeader,
    load,
    save,
    reset,
  };
}
